
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useCreateFarm, useUpdateFarm, useGetFarmById } from '@/hooks/use-farm';
import { Farm, FarmProduct } from '@/types/database.types';

// Import form components
import { farmFormSchema, FarmFormValues } from './form/FormSchema';
import BasicInfoFields from './form/BasicInfoFields';
import LocationCoordinatesFields from './form/LocationCoordinatesFields';
import FarmerInfoFields from './form/FarmerInfoFields';
import HarvestDateFields from './form/HarvestDateFields';
import FormActions from './form/FormActions';

const FarmForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  
  const { data: farm, isLoading: isFarmLoading } = useGetFarmById(id || '');
  const createFarm = useCreateFarm();
  const updateFarm = useUpdateFarm();

  const form = useForm<FarmFormValues>({
    resolver: zodResolver(farmFormSchema),
    defaultValues: {
      region: '',
      farm_size: 0,
      irrigation_type: null,
      latitude: null,
      longitude: null,
      
      // Farmer information fields
      farmer_name: '',
      phone_number: '',
      union_name: null,
      has_contract: false,
      has_phone_access: false,
      officer: null,
      product_name: '',
      products: [],
      distance_from_addis: 0,
      city: null,
      area: null,
      farm_category: undefined, // Will be set from database or user selection
      
      // Harvest date fields
      harvest_start_date: null,
      harvest_end_date: null,
      estimated_harvest_kg: null,
    },
  });

  React.useEffect(() => {
    if (isEditMode && farm) {
      form.reset({
        region: farm.region,
        farm_size: farm.land_size, // Map from land_size in database to farm_size in form
        irrigation_type: farm.irrigation_type,
        latitude: farm.latitude,
        longitude: farm.longitude,
        farmer_name: farm.farmer_name,
        phone_number: farm.phone_number,
        union_name: farm.union_name,
        has_contract: farm.has_contract,
        has_phone_access: farm.has_phone_access,
        officer: farm.officer,
        product_name: farm.product_name,
        // Don't map products array here as the form expects string[], not FarmProduct[]
        products: farm.products?.map(p => p.product_name) || [],
        distance_from_addis: farm.distance_from_addis,
        city: farm.city,
        area: farm.area,
        farm_category: farm.farm_category as 'Corporate Farm' | 'Large Scale Farmer' | 'Medium Scale Farmer' | 'Small Holder Farmer' || undefined,
        harvest_start_date: farm.harvest_start_date,
        harvest_end_date: farm.harvest_end_date,
        estimated_harvest_kg: farm.estimated_harvest_kg,
      });
    }
  }, [isEditMode, farm, form]);

  const onSubmit = async (values: FarmFormValues) => {
    try {
      // Convert farm_size from form to land_size for database
      const formattedValues = {
        ...values,
        land_size: values.farm_size,
      };
      
      // Remove farm_size and products from the values as they're not in the database schema
      const { farm_size, products, ...dataToSubmit } = formattedValues;
      
      if (isEditMode && id) {
        await updateFarm.mutateAsync({ id, data: dataToSubmit });
        toast({
          title: "Farm updated",
          description: "The farm has been successfully updated."
        });
      } else {
        // Use 'as any' to resolve type conflicts with the products field
        await createFarm.mutateAsync(dataToSubmit as any);
        toast({
          title: "Farm created",
          description: "The farm has been successfully created."
        });
      }
      navigate('/farms');
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to save farm. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isEditMode && isFarmLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">{isEditMode ? 'Edit Farm' : 'Add New Farm'}</h2>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <BasicInfoFields form={form} />
          
          <LocationCoordinatesFields form={form} />
          
          <FarmerInfoFields form={form} />
          
          <HarvestDateFields form={form} />

          <FormActions 
            isLoading={createFarm.isPending || updateFarm.isPending} 
            isEditMode={isEditMode} 
          />
        </form>
      </Form>
    </div>
  );
};

export default FarmForm;
